import { useEffect, useState } from 'react';
import { useParams as useRouterParams } from 'react-router-dom';

const API_BASE = import.meta.env.VITE_API_BASE ?? '';

function markdownToHtml(md: string): string {
  let html = md
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/^#{3}\s+(.+)$/gm, '<h3>$1</h3>')
    .replace(/^#{2}\s+(.+)$/gm, '<h2>$1</h2>')
    .replace(/^#{1}\s+(.+)$/gm, '<h1>$1</h1>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/^---$/gm, '<hr/>')
    .replace(/^- (.+)$/gm, '<li>$1</li>');

  html = html.replace(/(<li>.*<\/li>\n?)+/g, match => `<ul>${match}</ul>`);
  html = html.replace(/(\|.+\|\n\|[-: |]+\|\n(?:\|.+\|\n?)*)/g, (tableBlock) => {
    const lines = tableBlock.trim().split('\n').filter(l => l.trim() && !l.match(/^\|[-: |]+\|$/));
    if (lines.length === 0) return tableBlock;
    const [headerLine, ...bodyLines] = lines;
    const headers = headerLine.split('|').filter(Boolean).map(h => `<th>${h.trim()}</th>`).join('');
    const rows = bodyLines.map(line => {
      const cells = line.split('|').filter(Boolean).map(c => `<td>${c.trim()}</td>`).join('');
      return `<tr>${cells}</tr>`;
    }).join('');
    return `<table border="1" style="border-collapse:collapse;width:100%;margin:8px 0"><thead><tr>${headers}</tr></thead><tbody>${rows}</tbody></table>`;
  });
  html = html.replace(/^(?!<[h|u|t|l|h|d]).+$/gm, line =>
    line.trim() ? `<p style="margin:4px 0;line-height:1.6">${line}</p>` : ''
  );
  return html;
}

// ── Watermark Component ───────────────────────────────────────────────────────
function WatermarkOverlay({ text }: { text?: string }) {
  const display = text || `Erix One • Bảo Mật Doanh Nghiệp • ${new Date().toLocaleDateString('vi-VN')}`;
  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: 'none',
        zIndex: 9999,
        overflow: 'hidden',
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-around',
        alignContent: 'space-around',
        opacity: 0.12,
        userSelect: 'none'
      }}
    >
      {Array.from({ length: 16 }).map((_, i) => (
        <div 
          key={i} 
          style={{
            transform: 'rotate(-25deg)',
            fontSize: '18px',
            fontWeight: 700,
            color: '#000',
            margin: '40px',
            whiteSpace: 'nowrap',
            letterSpacing: '1px'
          }}
        >
          {display}
        </div>
      ))}
    </div>
  );
}

export function PublicShare() {
  const { shareId } = useRouterParams();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');

  // Folder Explorer State
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [folderHistory, setFolderHistory] = useState<{ id: string; name: string }[]>([]);
  const [previewFile, setPreviewFile] = useState<any>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  
  const loadShare = async (pwd?: string) => {
    try {
      setLoading(true);
      setError('');
      
      const res = await fetch(`${API_BASE}/api/shares/${shareId}`, {
        method: pwd ? 'POST' : 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('erixone_token') || localStorage.getItem('ericone_token') || ''}`
        },
        body: pwd ? JSON.stringify({ password: pwd }) : undefined
      });
      
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Không thể tải liên kết chia sẻ');
      
      setData(json);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (shareId) loadShare();
  }, [shareId]);

  // Load single file inside shared folder
  const openFilePreview = async (fileDoc: any) => {
    try {
      setPreviewLoading(true);
      const res = await fetch(`${API_BASE}/api/shares/${shareId}/file/${fileDoc.id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('erixone_token') || localStorage.getItem('ericone_token') || ''}`
        }
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Không thể mở tệp');
      setPreviewFile(json);
    } catch (e: any) {
      alert(e.message);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Block download shortcuts if allow_download is false
  useEffect(() => {
    if (data && data.allow_download === false) {
      const handleKeyDown = (e: KeyboardEvent) => {
        if ((e.ctrlKey || e.metaKey) && (e.key === 'p' || e.key === 's')) {
          e.preventDefault();
          alert('Chủ sở hữu đã khóa tính năng in và lưu tài liệu này.');
        }
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [data]);

  if (loading) {
    return (
      <div style={{ display:'flex', height:'100vh', alignItems:'center', justifyContent:'center', background:'#080816' }}>
        <div className="spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display:'flex', height:'100vh', alignItems:'center', justifyContent:'center', background:'#080816', color:'white' }}>
        <div style={{ textAlign:'center', background:'var(--bg-elevated)', padding:'40px', borderRadius:'16px', border:'1px solid var(--border)', maxWidth: '420px' }}>
          <span style={{ fontSize:'48px' }}>🔒</span>
          <h2 style={{ margin:'16px 0 8px 0', fontSize: '20px' }}>Không thể truy cập</h2>
          <p style={{ color:'var(--text-secondary)', fontSize: '14px', lineHeight: '1.6' }}>{error}</p>
        </div>
      </div>
    );
  }

  if (data?.requires_password) {
    return (
      <div style={{ display:'flex', height:'100vh', alignItems:'center', justifyContent:'center', background:'#080816', color:'white' }}>
        <div style={{ background:'var(--bg-elevated)', padding:'36px', borderRadius:'16px', border:'1px solid var(--border)', width:'380px', textAlign:'center' }}>
          <span style={{ fontSize:'42px', display:'block', marginBottom:'16px' }}>🔑</span>
          <h2 style={{ fontSize:'18px', marginBottom:'8px' }}>Tài liệu được bảo vệ bằng mật khẩu</h2>
          <p style={{ fontSize:'13px', color:'var(--text-secondary)', marginBottom:'20px' }}>{data.filename}</p>
          <input 
            type="password" 
            className="form-input" 
            placeholder="Nhập mật khẩu truy cập..." 
            value={password}
            onChange={e => setPassword(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && loadShare(password)}
            style={{ width:'100%', marginBottom:'16px', background: '#0f0f1d', color: 'white', boxSizing: 'border-box' }}
          />
          <button className="btn btn--primary" style={{ width:'100%' }} onClick={() => loadShare(password)}>Mở Khóa</button>
        </div>
      </div>
    );
  }

  // ── Render: Shared Folder Explorer (Drive Mode) ──────────────────────────────
  if (data?.is_folder) {
    const allItems: any[] = data.items || [];
    const activeParentId = currentFolderId || data.document_id;
    const currentItems = allItems.filter(item => {
      if (!currentFolderId) {
        return item.parent_id === data.document_id || !item.parent_id;
      }
      return item.parent_id === currentFolderId;
    });

    return (
      <div style={{ minHeight:'100vh', background:'#0b0b1a', color: '#f8fafc', display:'flex', flexDirection:'column' }}>
        {data.watermark_enabled && <WatermarkOverlay />}
        
        {/* Drive Header */}
        <header style={{ padding:'16px 32px', background:'rgba(15, 23, 42, 0.8)', backdropFilter: 'blur(12px)', borderBottom:'1px solid rgba(148, 163, 184, 0.15)', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap: 'wrap', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '28px' }}>📁</span>
            <div>
              <h1 style={{ fontSize:'18px', fontWeight:700, margin:0, color:'#f8fafc' }}>{data.filename}</h1>
              <span style={{ fontSize:'12px', color:'var(--text-secondary)' }}>Thư mục dùng chung qua Erix One Drive</span>
            </div>
          </div>
          <div style={{ display:'flex', gap:'8px', alignItems: 'center' }}>
            <span className="badge badge--purple">📂 Thư mục</span>
            <span className="badge badge--blue">{data.permissions === 'view' ? '👁️ Chỉ xem' : '✏️ Cho phép sửa'}</span>
            {!data.allow_download && <span className="badge badge--red">🚫 Khóa tải file</span>}
            {data.watermark_enabled && <span className="badge badge--green">🛡️ Watermark</span>}
          </div>
        </header>

        {/* Drive Explorer Body */}
        <main style={{ flex:1, padding:'28px 36px', maxWidth: '1200px', width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>
          
          {/* Breadcrumb Path */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 18px', background: 'rgba(30, 41, 59, 0.4)', borderRadius: '10px', border: '1px solid rgba(148, 163, 184, 0.15)', marginBottom: '20px' }}>
            <button 
              className="btn btn--ghost btn--sm" 
              style={{ fontSize: '13px', color: currentFolderId ? 'var(--text-secondary)' : 'var(--accent-cyan)', fontWeight: currentFolderId ? 400 : 600 }}
              onClick={() => {
                setCurrentFolderId(null);
                setFolderHistory([]);
              }}
            >
              🏠 Thư mục gốc ({data.filename})
            </button>
            {folderHistory.map((item, idx) => (
              <span key={item.id} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ color: 'var(--text-muted)' }}>/</span>
                <button 
                  className="btn btn--ghost btn--sm" 
                  style={{ fontSize: '13px', color: idx === folderHistory.length - 1 ? 'var(--accent-cyan)' : 'var(--text-secondary)', fontWeight: idx === folderHistory.length - 1 ? 600 : 400 }}
                  onClick={() => {
                    setCurrentFolderId(item.id);
                    setFolderHistory(prev => prev.slice(0, idx + 1));
                  }}
                >
                  📁 {item.name}
                </button>
              </span>
            ))}
            {folderHistory.length > 0 && (
              <button 
                className="btn btn--secondary btn--sm" 
                style={{ marginLeft: 'auto', fontSize: '11px', padding: '4px 10px' }}
                onClick={() => {
                  const parentHistory = folderHistory.slice(0, -1);
                  setFolderHistory(parentHistory);
                  setCurrentFolderId(parentHistory.length > 0 ? parentHistory[parentHistory.length - 1].id : null);
                }}
              >
                ⬅ Quay lại
              </button>
            )}
          </div>

          {/* Items Grid */}
          {currentItems.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px', background: 'rgba(15, 23, 42, 0.3)', borderRadius: '12px', border: '1px dashed rgba(148, 163, 184, 0.2)', color: 'var(--text-muted)' }}>
              <span style={{ fontSize: '40px', display: 'block', marginBottom: '8px' }}>📭</span>
              <p>Thư mục này hiện chưa có tệp tin hoặc thư mục con nào.</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '16px' }}>
              {currentItems.map(item => {
                const isFolderItem = item.source_type === 'folder';
                return (
                  <div 
                    key={item.id}
                    onClick={() => {
                      if (isFolderItem) {
                        setCurrentFolderId(item.id);
                        setFolderHistory(prev => [...prev, { id: item.id, name: item.filename }]);
                      } else {
                        openFilePreview(item);
                      }
                    }}
                    style={{
                      background: 'rgba(30, 41, 59, 0.5)',
                      borderRadius: '12px',
                      border: '1px solid rgba(148, 163, 184, 0.15)',
                      padding: '16px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      minHeight: '90px'
                    }}
                    onMouseEnter={e => (e.currentTarget.style.borderColor = 'var(--accent-cyan)')}
                    onMouseLeave={e => (e.currentTarget.style.borderColor = 'rgba(148, 163, 184, 0.15)')}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                      <span style={{ fontSize: '24px' }}>{isFolderItem ? '📁' : item.source_type === 'pdf' ? '📑' : '📄'}</span>
                      <div style={{ overflow: 'hidden' }}>
                        <div style={{ fontWeight: 600, fontSize: '14px', color: '#f8fafc', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.filename}>
                          {item.filename}
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                          {isFolderItem ? 'Thư mục con' : `${item.source_type.toUpperCase()} • ${item.chunk_count || 0} chunks`}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px', fontSize: '11px', color: 'var(--text-muted)' }}>
                      <span>{new Date(item.created_at).toLocaleDateString('vi-VN')}</span>
                      <span style={{ color: 'var(--accent-cyan)' }}>{isFolderItem ? 'Mở thư mục ➔' : 'Xem trước 👁️'}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </main>

        {/* Modal Xem Tệp Con Trong Thư Mục */}
        {previewFile && (
          <div className="modal-overlay" style={{ zIndex: 10000 }}>
            <div className="modal-content" style={{ width: '850px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', padding: '24px', background: '#0f172a', color: '#f8fafc', border: '1px solid rgba(148, 163, 184, 0.2)' }}>
              {previewFile.watermark_enabled && <WatermarkOverlay />}
              
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', borderBottom: '1px solid rgba(148, 163, 184, 0.15)', paddingBottom: '12px' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>{previewFile.filename}</h3>
                  <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Định dạng: {previewFile.source_type?.toUpperCase()}</span>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {previewFile.allow_download && (
                    <button 
                      className="btn btn--primary btn--sm"
                      onClick={() => {
                        const blob = new Blob([previewFile.content || ''], { type: 'text/markdown;charset=utf-8' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = previewFile.filename.endsWith('.md') ? previewFile.filename : `${previewFile.filename}.md`;
                        a.click();
                      }}
                    >
                      📥 Tải xuống (.md)
                    </button>
                  )}
                  <button className="btn btn--ghost btn--sm" onClick={() => setPreviewFile(null)}>✕ Đóng</button>
                </div>
              </div>

              <div 
                style={{ flex: 1, overflowY: 'auto', background: '#ffffff', color: '#0f172a', padding: '32px 40px', borderRadius: '8px', lineHeight: '1.7', fontFamily: 'Georgia, serif' }}
                dangerouslySetInnerHTML={{ __html: markdownToHtml(previewFile.content || '(Tài liệu trống hoặc chưa hoàn tất OCR)') }}
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Render: Single Document View ─────────────────────────────────────────────
  return (
    <div 
      style={{ minHeight:'100vh', background:'#f5f5f0', display:'flex', flexDirection:'column' }}
      onContextMenu={e => {
        if (data?.allow_download === false) {
          e.preventDefault();
        }
      }}
    >
      {data.watermark_enabled && <WatermarkOverlay />}

      <header style={{ padding:'12px 28px', background:'var(--bg-surface)', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <div>
          <h1 style={{ fontSize:'16px', fontWeight:600, margin:0, color:'var(--text-primary)' }}>{data.filename}</h1>
          <span style={{ fontSize:'11px', color:'var(--text-secondary)' }}>Được chia sẻ qua Erix One Drive</span>
        </div>
        <div style={{ display:'flex', gap:'8px', alignItems: 'center' }}>
          <span className="badge badge--blue">{data.permissions === 'view' ? '👁️ Chỉ xem' : '✏️ Cho phép chỉnh sửa'}</span>
          {!data.allow_download && <span className="badge badge--red">🚫 Chặn tải tệp</span>}
          {data.watermark_enabled && <span className="badge badge--green">🛡️ Watermark</span>}
        </div>
      </header>
      
      <main style={{ flex:1, overflow:'auto', padding:'40px' }}>
        <div style={{ maxWidth:'820px', margin:'0 auto', minHeight:'1050px', background:'white', boxShadow:'0 4px 24px rgba(0,0,0,0.08)', padding:'60px 72px', borderRadius:'6px' }}>
          {data.permissions === 'edit' ? (
            <div 
              contentEditable 
              suppressContentEditableWarning
              style={{ minHeight:'900px', outline:'none', fontSize:'14px', lineHeight:'1.75', fontFamily:'Georgia, serif', color:'#1a1a2e' }}
              dangerouslySetInnerHTML={{ __html: markdownToHtml(data.content || '') }}
            />
          ) : (
            <div 
              style={{ minHeight:'900px', outline:'none', fontSize:'14px', lineHeight:'1.75', fontFamily:'Georgia, serif', color:'#1a1a2e' }}
              dangerouslySetInnerHTML={{ __html: markdownToHtml(data.content || '') }}
            />
          )}
        </div>
      </main>
    </div>
  );
}
